import React, { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);

const AdMedia = ({ ad, active }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) v.play().catch(() => {});
    else v.pause();
  }, [active]);

  if (ad.media_type === 'video') {
    return (
      <video
        ref={videoRef}
        src={resolveUrl(ad.image_url)}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        className="w-full h-auto object-cover rounded-2xl shadow-lg"
        style={{ maxHeight: '400px' }}
        draggable={false}
        data-testid={`ad-video-${ad.id}`}
      />
    );
  }
  return (
    <img
      src={resolveUrl(ad.image_url)}
      alt={ad.title}
      className="w-full h-auto object-cover rounded-2xl shadow-lg"
      style={{ maxHeight: '400px' }}
      draggable={false}
      loading="lazy"
      data-testid={`ad-image-${ad.id}`}
    />
  );
};

const AUTOPLAY_MS = 9000;
const SWIPE_THRESHOLD = 50;

const AdvertisementCarousel = ({ advertisements }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const movedRef = useRef(false);
  const containerRef = useRef(null);

  const count = advertisements?.length || 0;

  const goTo = useCallback((idx) => {
    if (!count) return;
    setCurrentIndex(((idx % count) + count) % count);
  }, [count]);

  // Autoplay — reinicia a contagem sempre que o slide muda (manual ou automático)
  useEffect(() => {
    if (count <= 1 || dragging) return;
    const t = setTimeout(() => goTo(currentIndex + 1), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [currentIndex, count, dragging, goTo]);

  const onTouchStart = (e) => {
    startXRef.current = e.targetTouches[0].clientX;
    movedRef.current = false;
    setDragging(true);
  };

  const onTouchMove = (e) => {
    const delta = e.targetTouches[0].clientX - startXRef.current;
    if (Math.abs(delta) > 8) movedRef.current = true;
    // resistência nas bordas
    const atEdge = (currentIndex === 0 && delta > 0) || (currentIndex === count - 1 && delta < 0);
    setDragX(atEdge ? delta / 3 : delta);
  };

  const onTouchEnd = () => {
    setDragging(false);
    const delta = dragX;
    setDragX(0);
    if (delta <= -SWIPE_THRESHOLD && currentIndex < count - 1) goTo(currentIndex + 1);
    else if (delta >= SWIPE_THRESHOLD && currentIndex > 0) goTo(currentIndex - 1);
    else if (delta <= -SWIPE_THRESHOLD && currentIndex === count - 1) goTo(0);
    else if (delta >= SWIPE_THRESHOLD && currentIndex === 0) goTo(count - 1);
  };

  // bloqueia clique acidental depois de arrastar
  const onLinkClick = (e) => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (!count) return null;

  const width = containerRef.current?.offsetWidth || 0;
  const dragPct = width ? (dragX / width) * 100 : 0;

  return (
    <div className="relative z-10 w-full max-w-5xl mx-auto mb-8" data-testid="advertisement-carousel">
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-2xl select-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex"
          style={{
            transform: `translateX(calc(${-currentIndex * 100}% + ${dragPct}%))`,
            transition: dragging ? 'none' : 'transform 450ms cubic-bezier(0.25, 1, 0.35, 1)',
          }}
        >
          {advertisements.map((ad, idx) => (
            <div key={ad.id} className="w-full flex-shrink-0">
              {ad.link_url ? (
                <a
                  href={ad.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block cursor-pointer"
                  draggable={false}
                  onClick={onLinkClick}
                  data-testid={`ad-link-${ad.id}`}
                >
                  <AdMedia ad={ad} active={idx === currentIndex} />
                </a>
              ) : (
                <AdMedia ad={ad} active={idx === currentIndex} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Indicadores */}
      {count > 1 && (
        <div className="flex justify-center mt-3">
          <div className="flex items-center gap-1 rounded-full bg-black/25 backdrop-blur-md px-2 py-1 shadow-md">
            {advertisements.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className="p-1.5 group"
                data-testid={`slide-indicator-${index}`}
                aria-label={`Ir para anúncio ${index + 1}`}
              >
                <span
                  className={`block rounded-full transition-all duration-300 ${
                    currentIndex === index
                      ? 'w-6 h-2 bg-gradient-to-r from-orange-400 to-purple-500 shadow-[0_0_8px_rgba(251,146,60,0.7)]'
                      : 'w-2 h-2 bg-white/60 group-hover:bg-white group-hover:scale-125'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementCarousel;
